/**
 * P1-B · Attendance Source Configuration console.
 *
 * Route: /admin/attendance/source-config
 * Perm:  attendance:source_config:manage
 *
 * Purpose — a single-pane admin surface to answer three questions:
 *   1. Who is on which effective attendance source (manual / desktop_agent / hybrid)?
 *   2. Where is that decision coming from (override → policy → factory default)?
 *   3. Which desktop_agent/hybrid employees have NO active registration (misconfig)?
 *
 * Bulk-set flips employees.attendance_source_override in one governed
 * batch. Every effective change is stamped + recorded as a single AAS
 * action; no-op rows are reported clearly and never emit misleading
 * change events (P1-B contract).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    ArrowClockwise, ArrowsClockwise, CheckCircle, Info, MagnifyingGlass,
    UserGear, Warning, XCircle,
} from "@phosphor-icons/react";

import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
    DialogDescription,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";


// ─────────────────────────────────────────────────────────────────
// Tiny helpers
// ─────────────────────────────────────────────────────────────────


const MODE_STYLES = {
    manual: "bg-slate-100 text-slate-700 border-slate-300",
    desktop_agent: "bg-violet-100 text-violet-800 border-violet-300",
    hybrid: "bg-indigo-100 text-indigo-800 border-indigo-300",
};

const PRECEDENCE_STYLES = {
    override: "bg-amber-50 text-amber-800 border-amber-200",
    policy: "bg-slate-50 text-slate-600 border-slate-200",
    factory_default: "bg-slate-50 text-slate-500 border-slate-200",
};

const REG_STATUS_STYLES = {
    active: "bg-emerald-100 text-emerald-800 border-emerald-300",
    paused: "bg-slate-100 text-slate-700 border-slate-200",
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    revoked: "bg-rose-100 text-rose-800 border-rose-200",
    expired: "bg-slate-100 text-slate-500 border-slate-200",
    none: "bg-slate-50 text-slate-400 border-slate-200",
};

function ModeBadge({ value, "data-testid": testId }) {
    if (!value) return <span className="text-xs text-slate-400">—</span>;
    return (
        <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-medium ${MODE_STYLES[value] || MODE_STYLES.manual}`}
            data-testid={testId}>
            {value}
        </span>
    );
}

function PrecedenceBadge({ value }) {
    const label = value === "override"
        ? "Override"
        : value === "policy" ? "Policy"
        : "Default";
    return (
        <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium border ${PRECEDENCE_STYLES[value] || PRECEDENCE_STYLES.factory_default}`}>
            {label}
        </span>
    );
}

function RegBadge({ value }) {
    return (
        <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-medium ${REG_STATUS_STYLES[value] || REG_STATUS_STYLES.none}`}>
            {value}
        </span>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}


// ─────────────────────────────────────────────────────────────────
// Summary strip
// ─────────────────────────────────────────────────────────────────


function SummaryStrip({ summary }) {
    if (!summary) return null;
    const cells = [
        { label: "Active employees", value: summary.total_active_employees, tone: "slate" },
        { label: "Manual", value: summary.by_effective_mode?.manual ?? 0, tone: "slate" },
        { label: "Desktop Agent", value: summary.by_effective_mode?.desktop_agent ?? 0, tone: "violet" },
        { label: "Hybrid", value: summary.by_effective_mode?.hybrid ?? 0, tone: "indigo" },
        { label: "Active registrations", value: summary.active_registrations, tone: "emerald" },
        { label: "Misconfigurations", value: summary.misconfigured_count, tone: summary.misconfigured_count > 0 ? "rose" : "slate" },
    ];
    const toneMap = {
        slate: "bg-slate-50 text-slate-700 border-slate-200",
        rose: "bg-rose-50 text-rose-700 border-rose-200",
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        violet: "bg-violet-50 text-violet-700 border-violet-200",
        indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    };
    return (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="source-config-summary-strip">
            {cells.map((c) => (
                <div key={c.label}
                    className={`rounded-md border px-3 py-2 ${toneMap[c.tone] || toneMap.slate}`}
                    data-testid={`summary-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">{c.label}</div>
                    <div className="text-lg font-semibold">{c.value ?? 0}</div>
                </div>
            ))}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────
// Filter bar
// ─────────────────────────────────────────────────────────────────


function FilterBar({ filters, setFilters, onRefresh, loading }) {
    const setF = (k, v) => setFilters({ ...filters, [k]: v });
    return (
        <div className="grid gap-3 md:grid-cols-6" data-testid="source-config-filter-bar">
            <div className="md:col-span-2 relative">
                <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                    value={filters.search}
                    onChange={(e) => setF("search", e.target.value)}
                    placeholder="Search name / code / email…"
                    className="pl-9"
                    data-testid="filter-search"
                />
            </div>
            <select
                className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm"
                value={filters.effective_mode}
                onChange={(e) => setF("effective_mode", e.target.value)}
                data-testid="filter-effective-mode"
            >
                <option value="">All effective modes</option>
                <option value="manual">Manual only</option>
                <option value="desktop_agent">Desktop Agent only</option>
                <option value="hybrid">Hybrid only</option>
            </select>
            <select
                className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm"
                value={filters.override_filter}
                onChange={(e) => setF("override_filter", e.target.value)}
                data-testid="filter-override"
            >
                <option value="">Any override</option>
                <option value="__unset__">No override (policy)</option>
                <option value="manual">Override = manual</option>
                <option value="desktop_agent">Override = desktop_agent</option>
                <option value="hybrid">Override = hybrid</option>
            </select>
            <select
                className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm"
                value={filters.registration_status}
                onChange={(e) => setF("registration_status", e.target.value)}
                data-testid="filter-reg-status"
            >
                <option value="">Any reg status</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="pending">Pending</option>
                <option value="revoked">Revoked</option>
                <option value="expired">Expired</option>
                <option value="none">No registration</option>
            </select>
            <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-xs text-slate-700">
                    <input
                        type="checkbox"
                        checked={filters.misconfigured_only}
                        onChange={(e) => setF("misconfigured_only", e.target.checked)}
                        data-testid="filter-misconfigured-only"
                    />
                    Misconfig only
                </label>
                <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}
                    data-testid="btn-refresh-source-config">
                    <ArrowsClockwise className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────
// Bulk-set toolbar
// ─────────────────────────────────────────────────────────────────


function BulkSetBar({ selectedIds, onApply, disabled }) {
    const [target, setTarget] = useState("desktop_agent");
    const [reason, setReason] = useState("");
    const requiresReason = target === "desktop_agent" || target === "hybrid";
    const reasonOk = !requiresReason || reason.trim().length >= 10;
    if (!selectedIds.length) return null;
    return (
        <div className="flex flex-wrap items-end gap-3 rounded-md border-2 border-indigo-200 bg-indigo-50 p-3"
            data-testid="bulk-set-toolbar">
            <div className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800"
                data-testid="bulk-set-selected-count">
                {selectedIds.length} selected
            </div>
            <div>
                <Label htmlFor="bulk-target" className="text-xs">Set override to</Label>
                <select
                    id="bulk-target"
                    className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    data-testid="bulk-set-target"
                >
                    <option value="manual">Manual</option>
                    <option value="desktop_agent">Desktop Agent</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="__clear__">Clear (use policy default)</option>
                </select>
            </div>
            <div className="flex-1 min-w-[220px]">
                <Label htmlFor="bulk-reason" className="text-xs">
                    Reason {requiresReason ? "(min 10 chars)" : "(optional)"}
                </Label>
                <Input
                    id="bulk-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={
                        requiresReason
                            ? "Explain why this cohort is going to desktop_agent / hybrid…"
                            : "Optional context"
                    }
                    data-testid="bulk-set-reason"
                />
            </div>
            <Button
                onClick={() => onApply({ target, reason })}
                disabled={disabled || !reasonOk}
                data-testid="btn-bulk-set-apply"
            >
                Apply to {selectedIds.length}
            </Button>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────
// Grid
// ─────────────────────────────────────────────────────────────────


function Grid({ rows, selectedIds, setSelectedIds, onOpenAudit }) {
    if (!rows.length) {
        return (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-slate-500"
                data-testid="grid-empty">
                No employees match the current filters.
            </div>
        );
    }
    const allSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(r.employee_id));
    const toggleAll = () => {
        if (allSelected) {
            setSelectedIds(selectedIds.filter((id) => !rows.some((r) => r.employee_id === id)));
        } else {
            const add = rows.map((r) => r.employee_id);
            setSelectedIds(Array.from(new Set([...selectedIds, ...add])));
        }
    };
    const toggleOne = (id) => {
        setSelectedIds(
            selectedIds.includes(id)
                ? selectedIds.filter((x) => x !== id)
                : [...selectedIds, id],
        );
    };
    return (
        <div className="overflow-hidden rounded-md border bg-white" data-testid="source-config-grid">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                            <th className="px-3 py-2 w-8">
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={toggleAll}
                                    data-testid="grid-select-all"
                                    aria-label="Select all rows"
                                />
                            </th>
                            <th className="px-3 py-2">Employee</th>
                            <th className="px-3 py-2">Department / Designation</th>
                            <th className="px-3 py-2">Override</th>
                            <th className="px-3 py-2">Effective</th>
                            <th className="px-3 py-2">Applied policy</th>
                            <th className="px-3 py-2">Agent registration</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2 text-right">Audit</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                        {rows.map((r) => {
                            const checked = selectedIds.includes(r.employee_id);
                            return (
                                <tr key={r.employee_id}
                                    className={`${r.misconfigured ? "bg-rose-50/40" : "hover:bg-slate-50/60"}`}
                                    data-testid={`grid-row-${r.employee_id}`}>
                                    <td className="px-3 py-2 align-top">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleOne(r.employee_id)}
                                            data-testid={`row-select-${r.employee_id}`}
                                        />
                                    </td>
                                    <td className="px-3 py-2 align-top">
                                        <div className="font-medium text-slate-900">{r.employee_code}</div>
                                        <div className="text-xs text-slate-500">{r.full_name}</div>
                                        <div className="text-[10px] font-mono text-slate-400">{r.employee_id}</div>
                                    </td>
                                    <td className="px-3 py-2 align-top text-xs">
                                        <div className="text-slate-700">{r.department || "—"}</div>
                                        <div className="text-slate-500">{r.designation || "—"}</div>
                                    </td>
                                    <td className="px-3 py-2 align-top">
                                        {r.attendance_source_override
                                            ? <ModeBadge value={r.attendance_source_override}
                                                data-testid={`row-override-${r.employee_id}`} />
                                            : <span className="text-xs text-slate-400" data-testid={`row-override-${r.employee_id}`}>— unset —</span>}
                                        {r.override_stamp?.set_by_email ? (
                                            <div className="mt-1 text-[10px] text-slate-500">
                                                by {r.override_stamp.set_by_email} · {fmtDate(r.override_stamp.set_at)}
                                            </div>
                                        ) : null}
                                    </td>
                                    <td className="px-3 py-2 align-top">
                                        <div className="flex items-center gap-1.5">
                                            <ModeBadge value={r.effective_mode}
                                                data-testid={`row-effective-${r.employee_id}`} />
                                            <PrecedenceBadge value={r.precedence} />
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 align-top text-xs">
                                        {r.applied_policy_code ? (
                                            <>
                                                <div className="font-mono text-slate-700">{r.applied_policy_code}</div>
                                                <div className="text-slate-500">{r.applied_policy_name}</div>
                                                <div className="text-[10px] text-slate-400">
                                                    mode: <code>{r.applied_policy_mode}</code>
                                                </div>
                                            </>
                                        ) : <span className="text-slate-400">factory default (manual)</span>}
                                    </td>
                                    <td className="px-3 py-2 align-top text-xs">
                                        <div className="flex flex-col gap-1">
                                            <RegBadge value={r.registration?.status || "none"} />
                                            {r.registration?.machine_name ? (
                                                <div className="text-[10px] font-mono text-slate-500">
                                                    {r.registration.machine_name}
                                                </div>
                                            ) : null}
                                            {r.registration?.last_handshake_at ? (
                                                <div className="text-[10px] text-slate-400">
                                                    last: {fmtDate(r.registration.last_handshake_at)}
                                                </div>
                                            ) : null}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 align-top">
                                        {r.misconfigured ? (
                                            <div className="flex items-start gap-1"
                                                data-testid={`row-misconfig-${r.employee_id}`}>
                                                <Warning className="mt-0.5 h-3.5 w-3.5 text-rose-600 shrink-0" />
                                                <div className="text-[11px] text-rose-700">
                                                    <div className="font-medium">Misconfigured</div>
                                                    <div>Mode expects an active agent registration.</div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1 text-emerald-700"
                                                data-testid={`row-ok-${r.employee_id}`}>
                                                <CheckCircle className="h-3.5 w-3.5" />
                                                <span className="text-[11px]">OK</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 align-top text-right">
                                        <Button variant="outline" size="sm"
                                            onClick={() => onOpenAudit(r)}
                                            data-testid={`btn-open-audit-${r.employee_id}`}>
                                            History
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────


export default function AttendanceSourceConfig() {
    const { can } = useAuth();
    const isAllowed = can?.("attendance:source_config:manage");

    const [filters, setFilters] = useState({
        search: "",
        effective_mode: "",
        override_filter: "",
        registration_status: "",
        misconfigured_only: false,
    });
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [applying, setApplying] = useState(false);
    const [result, setResult] = useState(null);
    const [auditFor, setAuditFor] = useState(null);
    const [auditItems, setAuditItems] = useState([]);

    const refresh = useCallback(async () => {
        if (!isAllowed) return;
        setLoading(true);
        try {
            const params = {};
            for (const [k, v] of Object.entries(filters)) {
                if (v !== "" && v !== false) params[k] = v;
            }
            params.limit = 500;
            const [list, sum] = await Promise.all([
                api.get("/attendance/source-config", { params }),
                api.get("/attendance/source-config/summary"),
            ]);
            setRows(list.data.items || []);
            setSummary(sum.data);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load source-config");
        } finally {
            setLoading(false);
        }
    }, [filters, isAllowed]);

    useEffect(() => { refresh(); }, [refresh]);

    const applyBulk = async ({ target, reason }) => {
        if (!selectedIds.length) return;
        const override = target === "__clear__" ? null : target;
        const requiresReason = target === "desktop_agent" || target === "hybrid";
        if (requiresReason && reason.trim().length < 10) {
            toast.error("Reason must be ≥ 10 characters for desktop_agent / hybrid.");
            return;
        }
        setApplying(true);
        setResult(null);
        try {
            const r = await api.post("/attendance/source-config/bulk-set", {
                employee_ids: selectedIds,
                override,
                reason: reason.trim(),
            });
            setResult(r.data);
            if (r.data.changed_count > 0) {
                toast.success(
                    `Applied — ${r.data.changed_count} changed · ${r.data.noop_count} no-op · ${r.data.failed_count} failed. AAS ${r.data.aas_action_id || "—"}`,
                );
            } else if (r.data.failed_count > 0) {
                toast.error(`${r.data.failed_count} row(s) failed and ${r.data.noop_count} were no-ops.`);
            } else {
                toast(`All ${r.data.noop_count} row(s) were already set to that value — no changes made.`);
            }
            setSelectedIds([]);
            refresh();
        } catch (err) {
            toast.error(formatApiError(err) || "Bulk-set failed");
        } finally {
            setApplying(false);
        }
    };

    const openAudit = async (row) => {
        setAuditFor(row);
        setAuditItems([]);
        try {
            const r = await api.get(`/attendance/source-config/employee/${row.employee_id}/audit`);
            setAuditItems(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load audit");
        }
    };

    const visibleIds = useMemo(() => rows.map((r) => r.employee_id), [rows]);
    const trimmedSelection = useMemo(
        () => selectedIds.filter((id) => visibleIds.includes(id)),
        [selectedIds, visibleIds],
    );

    if (!isAllowed) {
        return (
            <EmptyState
                title="Not authorised"
                description="Attendance Source Configuration requires the `attendance:source_config:manage` capability."
                data-testid="source-config-forbidden"
            />
        );
    }

    return (
        <div className="space-y-6 pb-10" data-testid="source-config-page">
            <PageHeader
                eyebrow="ATTENDANCE · ADMIN"
                title="Attendance Source Configuration"
                description="Manage which employees have their attendance derived from the Desktop Agent bridge. Per-employee override takes precedence over the assigned policy. Every effective change is stamped and recorded as an Administrative Action."
                actions={
                    <Button variant="outline" size="sm" onClick={refresh} disabled={loading}
                        data-testid="btn-reload">
                        <ArrowsClockwise className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                }
            />

            <div className="space-y-4 px-6 sm:px-8">

                <SummaryStrip summary={summary} />

                <FilterBar
                    filters={filters}
                    setFilters={setFilters}
                    onRefresh={refresh}
                    loading={loading}
                />

                <BulkSetBar
                    selectedIds={trimmedSelection}
                    onApply={applyBulk}
                    disabled={applying}
                />

                <Grid
                    rows={rows}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    onOpenAudit={openAudit}
                />

                <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 flex items-start gap-2 text-xs text-blue-900"
                    data-testid="precedence-help-banner">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                        <div className="font-medium">Precedence order</div>
                        <div>
                            <code>employees.attendance_source_override</code> (this page) &rarr;
                            <code> attendance_policies.attendance_source_mode</code> (matched by
                            employee → designation → department → category → default) &rarr;
                            factory default <code>manual</code>. A “Misconfigured” row is one whose
                            resolved mode expects an active Desktop Agent registration but there is
                            none — the operator intent is preserved (no silent fallback).
                        </div>
                    </div>
                </div>
            </div>

            {/* Result dialog — surfaced after bulk-set completes. */}
            <Dialog open={!!result} onOpenChange={(o) => { if (!o) setResult(null); }}>
                <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto"
                    data-testid="bulk-set-result-dialog">
                    {result ? (
                        <>
                            <DialogHeader>
                                <DialogTitle>Bulk-set result · {result.bulk_op_id}</DialogTitle>
                                <DialogDescription>
                                    {result.changed_count} changed · {result.noop_count} no-op ·{" "}
                                    {result.failed_count} failed
                                    {result.aas_action_id ? <> · AAS <code>{result.aas_action_id}</code></> : null}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="overflow-hidden rounded-md border">
                                <table className="min-w-full divide-y divide-slate-200 text-sm">
                                    <thead className="bg-slate-50">
                                        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                                            <th className="px-3 py-2">Employee</th>
                                            <th className="px-3 py-2">Status</th>
                                            <th className="px-3 py-2">Previous</th>
                                            <th className="px-3 py-2">New</th>
                                            <th className="px-3 py-2">Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {result.per_employee.map((r) => (
                                            <tr key={r.employee_id}
                                                className={
                                                    r.status === "failed" ? "bg-rose-50/40"
                                                    : r.status === "changed" ? "bg-emerald-50/40"
                                                    : "bg-slate-50/40"
                                                }
                                                data-testid={`bulk-result-row-${r.employee_id}`}>
                                                <td className="px-3 py-2">
                                                    <div className="font-medium">{r.employee_code || "—"}</div>
                                                    <div className="text-[11px] text-slate-500">{r.employee_id}</div>
                                                </td>
                                                <td className="px-3 py-2 text-xs">
                                                    {r.status === "changed" ? (
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800">
                                                            <CheckCircle className="h-3 w-3" /> changed
                                                        </span>
                                                    ) : r.status === "failed" ? (
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-rose-800">
                                                            <XCircle className="h-3 w-3" /> failed
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                                                            no-op
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-xs">
                                                    {r.previous ? <ModeBadge value={r.previous} /> : <span className="text-slate-400">unset</span>}
                                                </td>
                                                <td className="px-3 py-2 text-xs">
                                                    {r.new ? <ModeBadge value={r.new} /> : <span className="text-slate-400">unset</span>}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-slate-600">
                                                    {r.error || r.aas_action_id || "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setResult(null)}
                                    data-testid="btn-bulk-set-result-close">
                                    Close
                                </Button>
                            </DialogFooter>
                        </>
                    ) : null}
                </DialogContent>
            </Dialog>

            {/* Employee audit history. */}
            <Dialog open={!!auditFor} onOpenChange={(o) => { if (!o) setAuditFor(null); }}>
                <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto"
                    data-testid="audit-dialog">
                    {auditFor ? (
                        <>
                            <DialogHeader>
                                <DialogTitle>Override history · {auditFor.employee_code}</DialogTitle>
                                <DialogDescription>
                                    Every effective flip of{" "}
                                    <code>attendance_source_override</code> for{" "}
                                    <span className="font-medium">{auditFor.full_name}</span>.
                                </DialogDescription>
                            </DialogHeader>
                            {auditItems.length === 0 ? (
                                <div className="rounded-md border border-dashed p-4 text-center text-sm text-slate-500"
                                    data-testid="audit-empty">
                                    No override history for this employee.
                                </div>
                            ) : (
                                <ul className="space-y-2 text-sm" data-testid="audit-list">
                                    {auditItems.map((a) => (
                                        <li key={a.audit_id}
                                            className="rounded-md border border-slate-200 bg-white p-3">
                                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                                <span>{fmtDate(a.created_at)}</span>
                                                <span>·</span>
                                                <span>{a.actor_email}</span>
                                                {a.aas_action_id ? (
                                                    <>
                                                        <span>·</span>
                                                        <span className="font-mono">{a.aas_action_id}</span>
                                                    </>
                                                ) : null}
                                                {a.route ? (
                                                    <>
                                                        <span>·</span>
                                                        <span className="rounded bg-slate-100 px-1.5 py-0.5">
                                                            {a.route}
                                                        </span>
                                                    </>
                                                ) : null}
                                            </div>
                                            <div className="mt-1 flex items-center gap-2 text-sm">
                                                {a.previous ? <ModeBadge value={a.previous} /> : <span className="text-slate-400">unset</span>}
                                                <span className="text-slate-400">→</span>
                                                {a.new ? <ModeBadge value={a.new} /> : <span className="text-slate-400">unset</span>}
                                            </div>
                                            {a.reason ? (
                                                <div className="mt-1 text-xs text-slate-600">
                                                    Reason: {a.reason}
                                                </div>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}
